import React from 'react';
import { isFeatureEnabled } from './features';
import { LayoutDashboard, Users, Building2, UserCheck, MapPin, FileText, Bell, Settings, BarChart3, ClipboardList, HelpCircle, FolderOpen, Bed, Calendar, Wrench, Sparkles, Globe, Star, Shield, Activity, Database, AlertTriangle, Key, Lock, FileEdit, Tag, UserPlus, Package, FolderTree, Award } from 'lucide-react';
// import { UserRole } from '@/types/auth';

// Riyal Icon Component for sidebar
const RiyalIcon = ({ className }: { className?: string }) => {
  return React.createElement('span', {
    className: `icon-saudi_riyal_new ${className || ''}`,
    style: { fontSize: '1.80rem' }
  });
};

export interface RouteConfig {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  feature: string;
  roles?: string[];
  children?: RouteConfig[];
  badge?: string | number;
}

export const allRoutes: RouteConfig[] = [
  // Dashboard 'Reports'
  { path: '/dashboard', label: 'Dashboard Reports', icon: FileText, feature: 'dashboard' },
  
  // E-commerce Management Routes
  { path: '/products', label: 'Product Manage', icon: Package, feature: 'products', roles: ['admin', 'super_admin'] },
  { path: '/categories', label: 'Categories Manage', icon: FolderTree, feature: 'categories', roles: ['admin', 'super_admin'] },
  { path: '/brands', label: 'Brands Manage', icon: Tag, feature: 'brands', roles: ['admin', 'super_admin'] },
  { path: '/orders', label: 'Orders Manage', icon: ClipboardList, feature: 'orders', roles: ['admin', 'super_admin', 'staff'] },
  { path: '/user-management', label: 'User Manage', icon: Users, feature: 'user_management', roles: ['admin', 'super_admin'] },
  { path: '/notifications', label: 'Notification Manage', icon: Bell, feature: 'notifications', roles: ['admin', 'super_admin', 'staff'] },
  
  // Additional routes (hidden from sidebar but available via direct navigation)
  { path: '/payments/logs', label: 'Payments • Logs', icon: RiyalIcon, feature: 'payments', roles: ['admin', 'super_admin'] },
  { path: '/settings', label: 'Settings', icon: Settings, feature: 'settings', roles: ['admin', 'super_admin'] },
  { path: '/profile-settings', label: 'Profile Settings', icon: UserPlus, feature: 'profile_settings', roles: ['admin', 'super_admin', 'staff'] },
];

export const getEnabledRoutes = (): RouteConfig[] => {
  return allRoutes.filter(route => isFeatureEnabled(route.feature as keyof import('./features').FeatureConfig));
};

export const getSidebarRoutes = (): RouteConfig[] => {
  return getEnabledRoutes().filter(route => 
    !['analytics', 'payments/logs', 'payments/transactions', 'reports', 'settings', 'profile-settings'].includes(route.path.slice(1))
  );
};

export const getMobileRoutes = (): RouteConfig[] => {
  return getEnabledRoutes().filter(route => 
    ['dashboard', 'rooms', 'bookings', 'users'].includes(route.path.slice(1))
  );
};

export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return allRoutes.find(route => route.path === path);
};

export const isRouteEnabled = (path: string): boolean => {
  const route = getRouteByPath(path);
  return route ? isFeatureEnabled(route.feature as keyof import('./features').FeatureConfig) : false;
};

export const getRouteLabel = (path: string): string => {
  const route = getRouteByPath(path);
  return route ? route.label : 'Unknown';
};

export const getRouteIcon = (path: string) => {
  const route = getRouteByPath(path);
  return route ? route.icon : LayoutDashboard;
};

export const getRoleBasedRoutes = (userRole: string): RouteConfig[] => {
  return allRoutes.filter(route => {
    // If no roles specified, allow all authenticated users
    if (!route.roles || route.roles.length === 0) {
      return true;
    }
    
    // Check if user role is in allowed roles
    return route.roles.includes(userRole);
  });
};

export const getCustomRoutes = (projectType: string): RouteConfig[] => {
  switch (projectType) {
    case 'hotel-booking':
      return [
        { path: '/rooms', label: 'Rooms', icon: Bed, feature: 'rooms' },
        { path: '/bookings', label: 'Bookings', icon: Calendar, feature: 'bookings' },
        { path: '/users', label: 'Users', icon: Users, feature: 'users' },
        { path: '/reviews', label: 'Reviews', icon: Star, feature: 'reviews' },

      ];
    case 'hotel':
      return [
        { path: '/rooms', label: 'Rooms', icon: Bed, feature: 'rooms' },
        { path: '/bookings', label: 'Bookings', icon: Calendar, feature: 'bookings' },
        { path: '/guests', label: 'Guests', icon: Users, feature: 'guests' },
        { path: '/housekeeping', label: 'Housekeeping', icon: Sparkles, feature: 'housekeeping' },
        { path: '/maintenance', label: 'Maintenance', icon: Wrench, feature: 'maintenance' },
      ];
    case 'restaurant':
      return [
        { path: '/menu', label: 'Menu', icon: FileText, feature: 'menu' },
        { path: '/orders', label: 'Orders', icon: ClipboardList, feature: 'orders' },
        { path: '/tables', label: 'Tables', icon: Building2, feature: 'tables' },
        { path: '/reservations', label: 'Reservations', icon: Calendar, feature: 'reservations' },
        { path: '/staff', label: 'Staff', icon: Users, feature: 'staff' },
      ];
    case 'ecommerce':
      return [
        { path: '/products', label: 'Products', icon: FileText, feature: 'products' },
        { path: '/orders', label: 'Orders', icon: ClipboardList, feature: 'orders' },
        { path: '/customers', label: 'Customers', icon: Users, feature: 'customers' },
        { path: '/inventory', label: 'Inventory', icon: FolderOpen, feature: 'inventory' },
        { path: '/analytics', label: 'Analytics', icon: BarChart3, feature: 'analytics' },
      ];
    default:
      return [];
  }
}; 